import { RefObject, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';

interface UseNotificationSyncParams {
  isOpen: boolean;
  activeTab: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  notificationListLength: number;
  refetch: () => void;
  fetchNextPage: () => void;
}

interface UseNotificationSyncReturn {
  rootReference: RefObject<Element | null>;
  loadMoreRef: (node?: Element | null) => void;
}

export const useNotificationSync = ({
  isOpen,
  activeTab,
  hasNextPage,
  isFetchingNextPage,
  notificationListLength,
  refetch,
  fetchNextPage,
}: UseNotificationSyncParams): UseNotificationSyncReturn => {
  const queryClient = useQueryClient();
  const rootReference = useRef<Element | null>(null);

  const { ref: loadMoreRef, inView } = useInView({
    root: rootReference.current,
    rootMargin: '100px',
  });

  useEffect(() => {
    if (isOpen) {
      queryClient.removeQueries({
        queryKey: ['notification-list', activeTab],
      });
      setTimeout(() => {
        refetch();
      }, 100);
    }
  }, [isOpen, queryClient, activeTab, refetch]);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && notificationListLength > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, notificationListLength, fetchNextPage]);

  return {
    rootReference,
    loadMoreRef,
  };
};
